<script setup lang="ts">
import { enUS } from 'date-fns/locale'
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker'

import { faCalendar } from '@fortawesome/pro-regular-svg-icons'

import { AppIcon, IconCopy } from '@shared/ui/icons'
import { AppText } from '@shared/ui/typography'
import { ModalDefault } from '@shared/ui/modals'
import { InputPrimary } from '@shared/ui/fields'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useModalCreateApiToken } from '../../../model/api-tokens/modals/modal-create-api-token.hook'

defineOptions({
  name: 'ModalCreateApiToken',
})

const {
  refIsOpen,
  refIsLoading,
  refName,
  refExpiresAt,
  refMinExpiresDate,
  refCreatedToken,
  refIsValid,
  setName,
  closeModal,
  createToken,
} = useModalCreateApiToken()
</script>

<template>
  <ModalDefault
    :show="refIsOpen"
    :loading="refIsLoading"
    :title="refCreatedToken ? 'Token created' : 'Create API token'"
    is-close-button
    @click:overlay="closeModal"
    @click:close="closeModal"
  >
    <template v-if="!refCreatedToken">
      <InputPrimary
        id="api-token-name"
        label="Token name"
        required
        placeholder="e.g. CI deploy key"
        :value="refName"
        @update:value="setName"
      />

      <div class="modal-create-api-token__expires">
        <AppText
          name="body2"
          color="slate-900"
          class="modal-create-api-token__expires-label"
        >
          Expires at
        </AppText>
        <Datepicker
          v-model="refExpiresAt"
          :locale="enUS"
          :week-start="1"
          :formats="{ input: 'yyyy-MM-dd' }"
          :time-config="{ enableTimePicker: false }"
          :min-date="refMinExpiresDate"
          auto-apply
          clearable
          placeholder="Never"
          teleport
        >
          <template #input-icon>
            <AppIcon :icon="faCalendar" />
          </template>
        </Datepicker>
      </div>
    </template>

    <template v-else>
      <AppText
        name="body2"
        color="slate-700"
        class="modal-create-api-token__hint"
      >
        Copy the token now — it will not be shown again.
      </AppText>
      <div class="modal-create-api-token__token-box">
        <AppText
          name="body2"
          color="slate-900"
          class="modal-create-api-token__token"
        >
          {{ refCreatedToken }}
        </AppText>
        <IconCopy
          :value="refCreatedToken"
          tooltip="Copy token"
        />
      </div>
    </template>

    <template #controls>
      <template v-if="!refCreatedToken">
        <BtnPrimary
          text="Create"
          color="emerald"
          :disabled="!refIsValid || refIsLoading"
          @click="createToken"
        />
        <BtnSecondary
          text="Cancel"
          @click="closeModal"
        />
      </template>
      <BtnPrimary
        v-else
        text="Done"
        color="emerald"
        @click="closeModal"
      />
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalCreateApiToken.scss" />
